// ── The Dashboard greeting ───────────────────────────────────────────────────
//
// It read "Welcome back, Nathan Garnett." for everyone, including someone who
// had just finished signing up and was seeing the product for the first time.
// Two things wrong with that:
//
//   "Welcome back"   said to someone who has never been here. It is the one
//                    greeting that cannot be true on a first visit, and it is
//                    the first sentence the product says to a new customer.
//   "Nathan Garnett" a full legal name. Nobody is greeted by their surname;
//                    it reads like a bank letter, not a welcome.
//
// So: first name only, and "Welcome" until there is something to come back to.

import { personNameFrom } from './personName';

const clean = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');

/**
 * The greeting line.
 *
 * The name is DROPPED rather than substituted when it is unknown. The previous
 * version fell back to a literal — `userData?.name || 'User'` — and greeted
 * people as "Welcome back, User.", which is worse than not naming them at all.
 *
 * @param firstName  the given name alone (see personName.js)
 * @param returning  false on someone's first visit to the dashboard
 */
export function welcomeMessage({ firstName, returning } = {}) {
  const name = clean(firstName);
  const opener = returning ? 'Welcome back' : 'Welcome';
  return name ? `${opener}, ${name}.` : `${opener}.`;
}

/** The greeting for a user record, first name taken the way every other screen takes it. */
export function greetingFor(userData, { returning } = {}) {
  return welcomeMessage({ firstName: personNameFrom(userData).first, returning });
}

// ── "Has this person been here before?" ──────────────────────────────────────
//
// There is no server-side signal for it: the user record carries created_at but
// no last_login_at or login_count, so the honest options were a per-browser
// flag or nothing. `created_at` was considered and rejected — it answers "is
// this account new", which is a different question. Someone who signs up, shuts
// the laptop and opens the dashboard the next morning is still arriving for the
// first time, and would have been told "welcome back".
//
// KNOWN LIMIT, and why it is acceptable: the flag is per browser, so a
// returning user on a new device is greeted "Welcome" again. That is the mild
// direction to be wrong in — a slightly-too-warm greeting, invisible to the
// person receiving it — whereas "Welcome back" to a brand new customer is the
// bug being fixed. If the API ever grows a login count, prefer it here and keep
// this as the fallback.
//
// Keyed by user id so two accounts sharing a browser each get their own first
// visit, rather than the second person inheriting the first person's.

const KEY = 'improveseo.seenDashboard';

const readSeen = () => {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    // Private browsing, storage disabled, or a value someone else wrote.
    return [];
  }
};

/**
 * Has this user opened the dashboard before, in this browser?
 *
 * Unknown id → true. A missing id means we cannot tell, and the safer answer is
 * the ordinary greeting: showing the once-only "Welcome" to an established
 * customer because their record had not loaded would be the more jarring error.
 */
export function hasSeenDashboard(userId) {
  if (userId == null || userId === '') return true;
  return readSeen().includes(String(userId));
}

/** Record that they have now. Safe to call on every render of the dashboard. */
export function markDashboardSeen(userId) {
  if (userId == null || userId === '') return;
  try {
    const seen = readSeen();
    const id = String(userId);
    if (seen.includes(id)) return;
    // Bounded: a shared machine should not grow this list forever.
    localStorage.setItem(KEY, JSON.stringify([...seen, id].slice(-20)));
  } catch (_) {
    // Storage unavailable. The greeting stays "Welcome" for this browser, which
    // is a cosmetic loss and not worth failing a render over.
  }
}

/**
 * The line under the greeting.
 *
 * It always read "Your SEO content engine is fine-tuned and ready. Let's grow
 * your visibility today." — including on an account with no website connected,
 * where nothing is fine-tuned, nothing is ready, and there is no engine. That
 * is the state where a new customer most needs telling what to do next, and the
 * one place the dashboard told them everything was already done.
 *
 * @param hasConnectedWebsite  true when at least one site is connected NOW —
 *   decided by isWebsiteConnected, the same rule the Websites tab, the status
 *   column and the notification bell use, so this line cannot claim a site is
 *   live while the row beneath it reads Not Connected.
 */
export function heroSubtitle({ hasConnectedWebsite } = {}) {
  return hasConnectedWebsite
    ? "Your SEO content engine is fine-tuned and ready. Let's grow your visibility today."
    : 'Connect a website, get your SEO content engine ready and start growing your visibility today!';
}
